import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { ContactsDashboardComponent } from 'src/app/modules/crm/contacts/contacts-dashboard/contacts-dashboard.component';
import { Customer } from 'src/app/modules/crm/touchpoints/entities/touchpoints-entity';
import { AddressService } from 'core/address/address.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { EmailPattern } from 'core/common-entities/email-pattern.class';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { UtilsService } from 'core/utils/utils.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import {
  QuoteNotificationAttributes,
  QuoteNotificationAttributeId
} from 'src/app/modules/shared/entities/quote-notification-attributes.enum';
import { ShortContact } from '../entities/short-contact.class';
import { EnsNotification } from '../entities/ens-notification.class';
import { EmailNotificationService } from '../services/email-notification.service';
import { PaymentDetailsService } from '../services/payment-details.service';
import { QuoteDetailsService } from '../services/quote-details.service';

@Component({
  selector: 'app-quote-email-notification',
  templateUrl: './quote-email-notification.component.html',
  styleUrls: ['./quote-email-notification.component.scss'],
  standalone: false
})
export class QuoteEmailNotificationComponent implements OnInit, OnDestroy {
  @Input() customerId = '';
  @Input() accountManagerId = '';
  @Input() isLead = false;
  @Output() goToPaymentDetails = new EventEmitter();

  appStrings: AppLocalization;
  maxNotifications = 5;
  notificationsForm = this.fb.group({
    notifications: this.fb.array([]),
  });
  contactDetailsForm: FormGroup;
  showAmEmailMessage = false;
  recipientTypes: { value: number, label: string }[] = [];
  attributesList: { value: number, label: string }[] = [];
  quoteCompleted = false;
  loadedByCustomer = false;
  contactEmail = '';
  isMobile: boolean;
  contactsList: ShortContact[] = [];
  displayContactsList: { value: string, label: string }[] = [];
  canEdit: boolean;
  quoteSaveInProgress = false;
  fieldMaxLengths = {
    contactName: 50,
    phoneNumber: 30,
    email: 100
  };
  private quoteId = '';
  private initialNotifications: EnsNotification[] = [];
  private subscriptions = new Subscription();
  private accountManagerEmail = '';
  private initialLoad = false;
  private panelClass = 'contacts-popUp';
  private leadId: string;
  private currentCustomer: any;
  private contactEmailFormGroup: UntypedFormGroup | null = null;
  get notifications() {
    return this.notificationsForm.controls.notifications as UntypedFormArray;
  }

  private readonly attributeControlMap = {
    [QuoteNotificationAttributeId.OrderAcknowledgement]: 'orderAcknowledgment',
    [QuoteNotificationAttributeId.Changes]: 'changes',
    [QuoteNotificationAttributeId.TrackingNumber]: 'trackingNumber',
    [QuoteNotificationAttributeId.Invoice]: 'invoice'
  };

  private hasContactSelection(value: string): boolean {
    return !!String(value ?? '').trim();
  }

  /**
   * Syncs attributes array to boolean controls
   * @param formGroup The notification form group
   */
  private syncAttributesToBooleans(formGroup: UntypedFormGroup) {
    const attributesValue = formGroup.controls.attributes.value;
    Object.entries(this.attributeControlMap).forEach(([attributeId, controlName]) => {
      const shouldBeChecked = attributesValue.includes(Number(attributeId));
      const control = formGroup.get(controlName);
      const currentValue = control?.value;

      if (currentValue !== shouldBeChecked) {
        control?.setValue(shouldBeChecked, { emitEvent: false });
      }
    });
  }

  /**
   * Syncs boolean controls to attributes array
   * @param formGroup The notification form group
   */
  private syncBooleansToAttributes(formGroup: UntypedFormGroup) {
    const attributes = Object.entries(this.attributeControlMap)
      .filter(([, controlName]) => {
        const controlValue = formGroup.get(controlName)?.value;
        return controlValue;
      })
      .map(([attributeId]) => Number(attributeId));

    formGroup.controls.attributes.setValue(attributes, { emitEvent: false });
  }

  constructor(
    private fb: UntypedFormBuilder,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private appInfoService: AppInfoService,
    private quoteDetailsService: QuoteDetailsService,
    private notificationService: NotificationService,
    private quotePaymentService: PaymentDetailsService,
    private emailNotificationService: EmailNotificationService,
    private utilsService: UtilsService,
    private deviceService: DeviceDetectorService,
    public addressService: AddressService,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.appStrings = this.appInfoService.appStrings;
    this.isMobile = this.deviceService.isMobile();
    this.quoteId = this.route.snapshot.paramMap.get('quoteId');
    this.leadId = this.route.snapshot.paramMap.get('leadId');
    this.canEdit = true;
    this.attributesList = this.utilsService.mapNumberKeyValueOptions(QuoteNotificationAttributes);

    // Initialize contactDetailsForm
    this.contactDetailsForm = this.formBuilder.group({
      contact: '',
      contactName: ['', [Validators.required, Validators.maxLength(this.fieldMaxLengths.contactName)]],
      phoneNumber: ['', [Validators.required, Validators.maxLength(this.fieldMaxLengths.phoneNumber)]],
      email: ['', [
        Validators.required,
        Validators.email,
        Validators.pattern(EmailPattern.pattern),
        Validators.maxLength(this.fieldMaxLengths.email)
      ]]
    });

    this.quoteDetailsService.getStaticValues(([templateValues]) => {
      if (templateValues.RecipientType) {
        this.recipientTypes = this.utilsService.mapNumberKeyValueOptions(templateValues.RecipientType);
      }
    });
    this.subscriptions.add(
      this.quoteDetailsService.setAccountManager$.subscribe(
        accountManagerId => this.handleAccountManagerChange(accountManagerId)
      )
    );
    this.subscriptions.add(this.quoteDetailsService.setCustomer$.subscribe((customer: Customer) => {
      this.currentCustomer = customer;
      if ((customer as any).isLead) {
        this.handleLeadChange(customer.id);
      } else {
        this.leadId = null;
        this.handleCustomerChange(customer.id);
        this.contactEmailChange(null);
      }
      this.getContactLists();
    }));
    this.subscriptions.add(this.quoteDetailsService.isCompleted$.subscribe(completed => {
      this.quoteCompleted = completed;

      if (completed) {
        this.notifications.disable();
        this.contactDetailsForm.disable();
      }
    }));
    this.subscriptions.add(this.quoteDetailsService.quoteIsSaving$.subscribe(value => {
      this.quoteSaveInProgress = value;
    }));
    this.subscriptions.add(this.quotePaymentService.savedContact$.subscribe(contact => {
      if (!contact?.name) {
        return;
      }

      this.getContactLists(() => {
        const matchingContact = this.contactsList.find(c => c.name === contact.name || c.email === contact.email);
        const contactId = matchingContact ? String(matchingContact.id) : null;
        this.populateContactForm(contactId, contact.name, contact.phone, contact.email, false);
      });
    }));
    if (this.quoteDetailsService.isExistingQuote(this.quoteId) || this.customerId) {
      this.initialLoad = true;
      if (this.customerId) {
        this.retrieveNotificationsByCustomer();
      } else {
        this.retrieveNotificationsByQuote();
      }
    } else {
      this.initialNotifications = this.notifications.value;
    }
  }

  /**
   * Sets the quoteId property with the id of the new quote
   * @param id id (guid) of the new quote
   */
  setQuoteId(id: string) {
    this.quoteId = id;
  }

  /**
   * Retrieves the saved notifications for an existing quote, based on Quote Id
   */
  private retrieveNotificationsByQuote() {
    this.emailNotificationService
      .getNotificationsByQuote(this.quoteId)
      .subscribe((savedNotifications: EnsNotification[]) => {
        this.populateNotificationsForm([...savedNotifications]);
        this.initialNotifications = this.notifications.getRawValue();
      });
  }

  /**
   * Retrieves the saved notifications for a specific customer
   */
  private retrieveNotificationsByCustomer() {
    this.emailNotificationService.getNotificationsByCustomer(this.customerId).subscribe(savedNotifications => {
      this.populateNotificationsForm([...savedNotifications]);
      this.initialNotifications = this.notifications.getRawValue();
      if (this.quoteId) {
        this.save();
      } else {
        this.loadedByCustomer = true;
      }
    });
  }

  /**
   * Populates the Notifications Form Array with retrieved data
   * @param notifications array of the retrieved notifications
   */
  private populateNotificationsForm(notifications: EnsNotification[]) {
    notifications.sort((a: EnsNotification, b: EnsNotification) => a.lineNumber - b.lineNumber);
    for (const notification of notifications) {
      const attributesArray = Object.entries(this.attributeControlMap)
        .filter(([, controlName]) => notification[controlName])
        .map(([attributeId]) => Number(attributeId));

      const formGroup = this.fb.group({
        id: this.fb.control(notification.id),
        email: this.fb.control(notification.email, [
          Validators.compose([
            Validators.required,
            Validators.email,
            Validators.pattern(EmailPattern.pattern),
          ]),
        ]),
        recipientTypeId: this.fb.control(notification.recipientTypeId, [
          Validators.required,
        ]),
        orderAcknowledgment: this.fb.control(notification.orderAcknowledgment),
        attributes: this.fb.control(attributesArray),
        changes: this.fb.control(notification.changes),
        trackingNumber: this.fb.control(notification.trackingNumber),
        invoice: this.fb.control(notification.invoice),
        lineNumber: this.fb.control(notification.lineNumber)
      });

      this.notifications.push(formGroup);
    }

    if (notifications.length && notifications[0].email) {
      this.accountManagerEmail = notifications[0].email;
      this.notifications.controls[0].get('email').disable();
    }

    if (this.quoteCompleted) {
      this.notifications.disable();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Adds a blank Notification row
   */
  addRow() {
    if (this.notifications.length >= this.maxNotifications) {
      return;
    }

    if (this.notifications.length === 0) {
      this.addFirstRow();
      return;
    }

    let emailValue = '';
    if (this.notifications.length === 1 && this.contactEmail) {
      emailValue = this.contactEmail;
    }

    this.notifications.push(this.generateNotificationRow(emailValue));
  }

  /**
   * Handles adding the first row for the array
   */
  private async addFirstRow() {
    if (!this.accountManagerEmail) {
      await this.retrieveAccountManagerEmail();
    }

    this.notifications.push(this.generateNotificationRow(this.accountManagerEmail));

    if (this.accountManagerEmail) {
      // If the Account Manager email was successfully retrieved, disable the email field
      this.notifications.controls[0].get('email').disable();
    }
  }

  /**
   * Retrieves the e-mail address of the currently selected Account Manager
   */
  private async retrieveAccountManagerEmail() {
    // Retrieve the Account Manager's email
    if (this.accountManagerId) {
      const amDetails = await this.emailNotificationService.getAccountManagerEmail(this.accountManagerId);
      this.accountManagerEmail = amDetails?.email;
    }

    // No email was found for the Account Manager, display a message
    if (!this.accountManagerEmail) {
      this.showAmEmailMessage = true;
    } else {
      this.showAmEmailMessage = false;
    }

    return this.accountManagerEmail;
  }

  /**
   * Generates a new FormGroup to insert in the notifications array
   * @param email initial email value to set
   */
  private generateNotificationRow(email = '') {
    return this.fb.group({
      id: this.fb.control(''),
      email: this.fb.control(email, [
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.pattern(EmailPattern.pattern),
        ]),
      ]),
      recipientTypeId: this.fb.control(1, [Validators.required]),
      attributes: this.fb.control(Object.keys(this.attributeControlMap).map(Number)),
      orderAcknowledgment: this.fb.control(true),
      changes: this.fb.control(true),
      trackingNumber: this.fb.control(true),
      invoice: this.fb.control(true),
    });
  }

  /**
   * Checks whether the Notifications array was modified
   */
  hasChanges() {
    return (
      JSON.stringify(this.initialNotifications) !==
      JSON.stringify(this.notifications.getRawValue()) ||
      !!this.contactDetailsForm?.dirty
    );
  }

  /**
   * Removes a Notification row
   * @param index index of the row in the formArray
   */
  deleteRow(index: number) {
    if (!this.showAmEmailMessage && index === 0 && this.notifications.length > 1) {
      return;
    }

    const removedControl = this.notifications.at(index) as UntypedFormGroup;
    if (removedControl === this.contactEmailFormGroup) {
      this.contactEmailFormGroup = null;
    }
    this.notifications.removeAt(index);

    if (!this.notifications.length) {
      this.showAmEmailMessage = false;
    }
  }

  /**
   * Clears all the Notifications
   */
  clearAll() {
    this.notifications.clear();
    this.showAmEmailMessage = false;
    this.contactEmailFormGroup = null;
  }

  /**
   * Checks if email notification rows are valid (silently, no notification, no contact check).
   * Used for complete quote — contact details are not required at this stage.
   */
  areEmailRowsValid(): boolean {
    this.notifications.controls.forEach((control) => {
      const formGroup = control as UntypedFormGroup;
      this.syncAttributesToBooleans(formGroup);
    });
    this.notifications.markAllAsTouched();
    return this.notifications.valid;
  }

  /**
   * Checks if notifications and contact details are valid (silently, no notification).
   * Used for complete order — contact details are required.
   */
  isValid(): boolean {
    const notificationsValid = this.notifications.valid;
    const contactDetailsValid = !this.contactDetailsForm || this.contactDetailsForm.valid;
    return notificationsValid && contactDetailsValid;
  }

  /**
   * Marks all notification and contact fields as touched to show validation errors
   */
  markAllAsTouched() {
    this.notifications.controls.forEach((control) => {
      const formGroup = control as UntypedFormGroup;
      this.syncAttributesToBooleans(formGroup);
    });
    this.notifications.markAllAsTouched();
    this.contactDetailsForm?.markAllAsTouched();
  }

  /**
   * Validates notifications and contact details with user notification on error
   */
  areNotificationsValid() {
    this.markAllAsTouched();

    if (!this.isValid()) {
      this.notificationService.showNotification(
        this.appStrings.order_completeErrorsMessage,
        NotificationType.Error
      );
      return false;
    }

    return true;
  }

  /**
   * Saves the values of the Form Array
   */
  save(quoteId = '', actionAfterSave?: () => void) {
    this.loadedByCustomer = false;
    if (this.quoteCompleted) {
      return;
    }

    this.notifications.controls.forEach((control) => {
      const formGroup = control as UntypedFormGroup;
      this.syncAttributesToBooleans(formGroup);
    });

    const formValues: EnsNotification[] = this.notifications.getRawValue();

    formValues.forEach((notification, index) => {
      if (!notification.id) {
        delete notification.id;
      }
      notification.quoteId = quoteId || this.quoteId;
      notification.lineNumber = index + 1;
    });
    this.emailNotificationService
      .updateEnsNotifications(
        quoteId || this.quoteId,
        this.customerId,
        formValues
      )
      .subscribe(savedNotifications => {
        savedNotifications.sort(
          (a: EnsNotification, b: EnsNotification) =>
            a.lineNumber - b.lineNumber
        );
        savedNotifications.forEach((notification, index) => {
          const formGroup = this.notifications.controls[index] as UntypedFormGroup;
          if (formGroup) {
            const currentAttributes = formGroup.controls.attributes.value;
            formGroup.patchValue(notification);
            if (currentAttributes) {
              formGroup.controls.attributes.setValue(currentAttributes);
            }
            this.syncBooleansToAttributes(formGroup);
          }
        });
        this.initialNotifications = this.notifications.getRawValue();
        this.contactDetailsForm?.markAsPristine();
        if (actionAfterSave) {
          actionAfterSave();
        }
      });
  }

  /**
   * Retrieves the new Account Manager e-mail if the AM was changed in the Quote Details section
   * @param newAmId employeeId of the new Account Manager
   */
  private async handleAccountManagerChange(newAmId: string) {
    const amChanged = this.accountManagerId !== newAmId;
    this.accountManagerId = newAmId;
    if (amChanged) {
      const amEmail = await this.retrieveAccountManagerEmail();

      if (this.notifications.length && !this.quoteCompleted) {
        if (amEmail) {
          this.notifications.controls[0].get('email').disable();
        } else if (!this.quoteCompleted) {
          this.notifications.controls[0].get('email').enable();
        }
      }
    }
  }

  /**
   * Fetches a customer's ENS Notifications when the customer is changed in the Quote Details section
   * @param newCustomerId id (guid) of the new Customer
   */
  private handleCustomerChange(newCustomerId: string) {
    if (this.customerId === newCustomerId) {
      return;
    }

    this.customerId = newCustomerId;
    if (this.initialLoad) {
      this.initialLoad = false;
      return;
    }

    this.contactEmailFormGroup = null;
    this.notifications.clear();
    this.retrieveNotificationsByCustomer();
  }

  /**
   * Handles lead change in the Quote Details section
   * @param newLeadId id (guid) of the new Lead
   */
  private handleLeadChange(newLeadId: string) {
    if (this.leadId === newLeadId) {
      return;
    }

    this.customerId = '';
    this.leadId = newLeadId;
    if (this.initialLoad) {
      this.initialLoad = false;
      return;
    }

    this.contactEmailFormGroup = null;
    this.notifications.clear();

    if (this.quoteDetailsService.isExistingQuote(this.quoteId)) {
      this.retrieveNotificationsByQuote();
    }
  }

  /**
   * Gets the list of contacts for the customer or lead
   */
  private getContactLists(callback?: () => void) {
    if (this.customerId) {
      this.quotePaymentService.getCustomerContacts(this.customerId, contacts => {
        this.contactsList = contacts;
        this.displayContactsList = this.utilsService.mapObjectToSelect(this.contactsList, 'id', 'name');
        if (callback) {
          callback();
        }
      });
    } else if (this.leadId) {
      this.quotePaymentService.getLeadContacts(this.leadId, contacts => {
        this.contactsList = contacts;
        this.displayContactsList = this.utilsService.mapObjectToSelect(this.contactsList, 'id', 'name');
        if (callback) {
          callback();
        }
      });
    }
  }

  /**
   * Populates the contact details form fields and defers setting the dropdown value
   * to allow the autocomplete to process the updated options list first.
   * @param contactId - The contact id to set in the dropdown (or null)
   * @param name - Contact name
   * @param phone - Contact phone number
   * @param email - Contact email
   * @param markDirty - Whether to mark the form as dirty (true for user actions)
   */
  private populateContactForm(contactId: string, name: string, phone: string, email: string, markDirty: boolean) {
    this.contactDetailsForm.patchValue({
      contactName: name,
      phoneNumber: phone,
      email
    });
    this.contactEmailChange(email, markDirty);
    if (markDirty) {
      this.contactDetailsForm.markAsDirty();
    }
    setTimeout(() => {
      this.contactDetailsForm.controls.contact.setValue(contactId);
    });
  }

  /**
   * Set contact fields based on selected contact
   * @param contact - The selected contact
   */
  setContactFields(contact: ShortContact) {
    if (typeof contact === 'undefined') {
      return;
    }

    this.contactDetailsForm.controls.contact.setValue(contact.id);
    this.contactDetailsForm.controls.contactName.setValue(contact.name);
    this.contactDetailsForm.controls.contactName.markAsPristine();
    if (contact.phones.length) {
      this.contactDetailsForm.controls.phoneNumber.setValue(contact.phones[0]);
    } else {
      this.contactDetailsForm.controls.phoneNumber.setValue(null);
    }
    this.contactDetailsForm.controls.email.setValue(contact.email);
    this.contactEmailChange(contact.email, true);
  }

  /**
   * Clears the contact details typed against a contact selection
   */
  private clearContactFields() {
    this.contactDetailsForm.controls.contactName.setValue('');
    this.contactDetailsForm.controls.phoneNumber.setValue('');
    this.contactDetailsForm.controls.email.setValue('');
    this.contactEmailChange(null);
  }

  /**
   * Clears the contact details when the user empties the contact search field.
   * @param value - The text currently typed in the contact search field
   */
  onContactSearchChange(value: string) {
    if (!this.hasContactSelection(value)) {
      this.clearContactFields();
    }
  }

  /**
   * Set contact fields based on selected contact ID (mobile version)
   * @param contactId - The selected contact ID
   */
  setContactFieldMobile(contactId: string) {
    const selectedContact = this.contactsList.find(c => c.id === contactId);
    this.setContactFields(selectedContact);
  }

  /**
   * Opens the create contact dialog
   */
  openCreateContactDialog() {
    let mappedFirstName;
    let mappedMiddleName;
    let mappedLastName;
    const fullContactName = this.contactDetailsForm.controls.contactName.value;
    if (this.contactDetailsForm.controls.contactName.value) {
      [mappedFirstName, mappedMiddleName, mappedLastName] = this.addressService.processingFullContactName(fullContactName);
    }
    const createContact = this.dialog.open(ContactsDashboardComponent, {
      panelClass: this.panelClass,
      disableClose: true,
      data: {
        contactFirstName: mappedFirstName || '',
        contactMiddleName: mappedMiddleName || '',
        contactLastName: mappedLastName || '',
        phoneNumberFromQuote: this.contactDetailsForm.controls.phoneNumber.value,
        contactEmailFromQuote: this.contactDetailsForm.controls.email.value,
        createContactDialog: true,
        customerIdDialog: this.customerId || '',
        leadIdDialog: this.leadId || '',
        customerDetails: this.currentCustomer
      }
    });
    createContact.afterClosed().subscribe(dialogAnswer => {
      // When the user wants to save the contact and he clicks on the "Save" button
      if (dialogAnswer === 1) {
        this.getContactLists(() => {
          const selectedContact: ShortContact = this.contactsList[this.contactsList.length - 1];
          const phone = selectedContact.phones?.length ? selectedContact.phones[0] : null;
          this.populateContactForm(String(selectedContact.id), selectedContact.name, phone, selectedContact.email, true);
        });
      }
    });
  }

  /**
   * Handles contact email changes
   * @param newEmail new value of the email field
   * @param userChanged whether the user changed the email
   */
  contactEmailChange(newEmail: string, userChanged = false) {
    this.contactEmail = newEmail;

    if (!userChanged || this.quoteCompleted || this.isLead) {
      return;
    }

    if (!newEmail) {
      this.contactEmailFormGroup?.controls.email.setValue('');
      return;
    }

    if (this.contactEmailFormGroup) {
      this.contactEmailFormGroup.controls.email.setValue(newEmail);
    } else {
      this.addContactEmailAsNotification(newEmail);
    }
  }

  /**
   * Adds the contact email as a new notification entry if not already present
   * @param email The email to add
   */
  private addContactEmailAsNotification(email: string) {
    const emailControl = this.contactDetailsForm.controls.email;
    if (!emailControl.valid) {
      return;
    }

    if (this.notifications.length >= this.maxNotifications) {
      return;
    }

    const existingMatchingControl = this.notifications.controls.find(control => {
      const notificationEmail = control.get('email')?.value?.toLowerCase();
      return notificationEmail === email.toLowerCase();
    }) as UntypedFormGroup | undefined;

    if (existingMatchingControl) {
      this.contactEmailFormGroup = existingMatchingControl;
      return;
    }

    if (this.notifications.length === 0) {
      this.addFirstRow().then(() => {
        const newGroup = this.generateNotificationRow(email);
        this.notifications.push(newGroup);
        this.contactEmailFormGroup = newGroup;
      });
    } else {
      const newGroup = this.generateNotificationRow(email);
      this.notifications.push(newGroup);
      this.contactEmailFormGroup = newGroup;
    }
  }

  /**
   * Gets contact details for saving
   */
  getContactDetails() {
    const contactDetails = this.contactDetailsForm.getRawValue();

    if (contactDetails.contact || contactDetails.contactName || contactDetails.phoneNumber || contactDetails.email) {
      return {
        id: contactDetails.contact || this.appInfoService.emptyGuid,
        name: contactDetails.contactName,
        phone: contactDetails.phoneNumber,
        email: contactDetails.email
      };
    }
    return null;
  }
}
